import React, { useEffect, useRef, useState } from 'react';
import ParallaxBackground from './ParallaxBackground';
import { FiMail, FiPhone, FiMapPin, FiClock, FiSend } from 'react-icons/fi';

const ContactForm = ({ backgroundImage = '/images/benfranklinbridge.jpg' }) => {
  const formContainerRef = useRef(null);
  const [formLoaded, setFormLoaded] = useState(false);
  const [showRefreshHint, setShowRefreshHint] = useState(false);

  useEffect(() => {
    setFormLoaded(false);
    setShowRefreshHint(false);

    let script = document.getElementById('deftform-embed-script');
    if (!script) {
      script = document.createElement('script');
      script.id = 'deftform-embed-script';
      script.src = 'https://cdn.deftform.com/embed.js';
      script.async = true;
      document.body.appendChild(script);
    }

    const checkFormLoaded = () => {
      if (formContainerRef.current) {
        const formDiv = formContainerRef.current.querySelector('.deftform');
        const hasContent = formDiv && (
          formDiv.querySelector('iframe') ||
          formDiv.querySelector('form') ||
          formDiv.children.length > 0
        );
        if (hasContent) {
          setFormLoaded(true);
          return true;
        }
      }
      return false;
    };

    const initializeDeftform = () => {
      setTimeout(() => {
        if (typeof window.reinitDeftform === 'function') {
          window.reinitDeftform();
        } else if (typeof window.initDeftform === 'function') {
          window.initDeftform();
        } else {
          setTimeout(() => {
            if (typeof window.reinitDeftform === 'function') {
              window.reinitDeftform();
            } else if (typeof window.initDeftform === 'function') {
              window.initDeftform();
            }
          }, 1000);
        }
      }, 300);
    };

    initializeDeftform();

    const pollInterval = setInterval(() => {
      if (checkFormLoaded()) {
        clearInterval(pollInterval);
      }
    }, 500);

    const pollTimeout = setTimeout(() => {
      clearInterval(pollInterval);
      if (!formLoaded) {
        setShowRefreshHint(true);
      }
    }, 10000);

    return () => {
      clearInterval(pollInterval);
      clearTimeout(pollTimeout);
    };
  }, []);

  return (
    <ParallaxBackground
      image={backgroundImage}
      className="py-24 px-4"
      overlay={true}
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Contact Info - Left Side */}
          <div className="text-white space-y-8">
            <div>
              <h2 className="font-display text-4xl md:text-5xl font-bold mb-4">
                Let's Start Your Closing Journey
              </h2>
              <p className="font-body text-lg text-gray-200 leading-relaxed">
                Whether you're buying, selling, or transferring property, we're here to make your closing seamless.
              </p>
            </div>

            <div className="space-y-6">
              <a
                href="tel:4845808865"
                className="flex items-center gap-4 p-4 bg-white/10 backdrop-blur-md rounded-xl border border-white/20 hover:bg-white/15 transition-all group"
              >
                <div className="w-12 h-12 bg-eagle-sky/30 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                  <FiPhone className="h-6 w-6 text-eagle-sky" />
                </div>
                <div>
                  <div className="text-sm text-gray-300">Call Us</div>
                  <div className="font-semibold text-lg">484-580-8865</div>
                </div>
              </a>

              <a
                href="mailto:info@eagleclosing.com"
                className="flex items-center gap-4 p-4 bg-white/10 backdrop-blur-md rounded-xl border border-white/20 hover:bg-white/15 transition-all group"
              >
                <div className="w-12 h-12 bg-eagle-sky/30 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                  <FiMail className="h-6 w-6 text-eagle-sky" />
                </div>
                <div>
                  <div className="text-sm text-gray-300">Email Us</div>
                  <div className="font-semibold text-lg">info@eagleclosing.com</div>
                </div>
              </a>

              <div className="flex items-center gap-4 p-4 bg-white/10 backdrop-blur-md rounded-xl border border-white/20">
                <div className="w-12 h-12 bg-eagle-sky/30 rounded-full flex items-center justify-center">
                  <FiMapPin className="h-6 w-6 text-eagle-sky" />
                </div>
                <div>
                  <div className="text-sm text-gray-300">Location</div>
                  <div className="font-semibold text-lg">Wayne, PA 19087</div>
                </div>
              </div>

              <div className="flex items-center gap-4 p-4 bg-white/10 backdrop-blur-md rounded-xl border border-white/20">
                <div className="w-12 h-12 bg-eagle-sky/30 rounded-full flex items-center justify-center">
                  <FiClock className="h-6 w-6 text-eagle-sky" />
                </div>
                <div>
                  <div className="text-sm text-gray-300">Hours</div>
                  <div className="font-semibold text-lg">Mon-Fri: 9AM - 5PM</div>
                </div>
              </div>
            </div>

            <blockquote className="border-l-4 border-eagle-sky pl-6 italic text-gray-200">
              "Successful real estate closings rely on the support of a dependable title company. Make sure yours is up to the task!"
            </blockquote>
          </div>

          {/* Glassmorphism Form - Right Side */}
          <div className="relative">
            {/* Glassmorphism Card */}
            <div
              ref={formContainerRef}
              className="bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl p-8"
              style={{
                background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
              }}
            >
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-eagle-sky/30 rounded-full flex items-center justify-center">
                  <FiSend className="h-5 w-5 text-white" />
                </div>
                <h3 className="font-display text-2xl font-bold text-white">
                  Send Us a Message
                </h3>
              </div>
              <p className="text-gray-200 mb-6 text-sm">
                Fill out the form below and we'll get back to you within 24 hours.
              </p>

              {showRefreshHint && !formLoaded && (
                <div className="mb-4 p-3 bg-amber-500/20 backdrop-blur-sm border border-amber-400/30 rounded-lg">
                  <p className="text-amber-200 text-sm text-center">
                    Form not loading? Please refresh the page.
                  </p>
                </div>
              )}

              <div
                className="deftform"
                data-form-id="cc1ccd5b-0b5a-4db3-bbc4-a65cc2a19b62"
                data-form-width="100%"
                data-form-align="left"
                data-form-auto-height="1"
              />
            </div>

            {/* Decorative glow */}
            <div className="absolute -top-4 -right-4 w-32 h-32 bg-eagle-sky/30 rounded-full filter blur-2xl" />
            <div className="absolute -bottom-4 -left-4 w-24 h-24 bg-eagle-gold/20 rounded-full filter blur-2xl" />
          </div>
        </div>
      </div>
    </ParallaxBackground>
  );
};

export default ContactForm;
